import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, RefreshCw, Link2, Link2Off, ExternalLink, MapPin, MousePointerClick, ArrowUpDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "../components/common";

/** External Benchmark card — compares the Servantage SSI with Google rating,
 *  area peers and segment peers for a single location.
 *
 *  Dormant state: when Google isn't configured, the card renders a quiet
 *  "connect to activate" hint and the Match/Refresh buttons stay hidden.
 */
export default function ExternalBenchmark({ locationId, ssi, canEdit = true }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drillBusy, setDrillBusy] = useState(null); // 'google' | 'area' | 'segment' | null
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [segmentOpen, setSegmentOpen] = useState(false);
  const navigate = useNavigate();

  const load = async (opts = {}) => {
    try {
      const { data } = await api.get(`/admin/locations/${locationId}/benchmark`, { params: opts });
      setData(data);
      return data;
    } catch (e) {
      // 403 for store admins on other locations is fine — just hide the card.
      setData({ configured: false, hidden: true });
      return null;
    }
  };

  useEffect(() => { if (locationId) load(); }, [locationId]);

  // Open a drill-through modal with a live refresh so admins always see
  // the newest Google data (reviews, area peers, segment peers).
  const openDrill = async (kind) => {
    setDrillBusy(kind);
    const openMap = { google: setReviewsOpen, area: setAreaOpen, segment: setSegmentOpen };
    try {
      await load({ refresh: true });
    } catch (e) { /* fall back to cached data */ }
    finally {
      setDrillBusy(null);
      openMap[kind](true);
    }
  };

  if (!data || data.hidden) return null;

  const linked = Boolean(data.place_id);
  const configured = Boolean(data.configured);

  const match = async () => {
    setBusy(true);
    try {
      const { data: fresh } = await api.post(`/admin/locations/${locationId}/google/match`);
      setData(fresh);
      toast.success(`Linked to ${fresh.display_name || "Google place"}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      const { data: fresh } = await api.post(`/admin/locations/${locationId}/google/refresh`);
      setData(fresh);
      toast.success("Benchmarks refreshed");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("Unlink this location from Google?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/locations/${locationId}/google/unlink`);
      setData({ configured });
      toast.success("Unlinked");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  // ----- dormant / not configured AND no cached data -----
  if (!configured && !linked) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5" data-testid="ext-benchmark-dormant">
        <div className="flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 text-slate-400" />
          <div className="flex-1">
            <Label>External Benchmark</Label>
            <p className="mt-1 text-sm font-semibold text-slate-700">Compare your Service Index against Google</p>
            <p className="mt-1 text-xs text-slate-500">
              Pulls this store's Google rating plus area &amp; segment averages. Add a Google Maps API key to activate.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ----- not linked yet (key is configured) -----
  if (!linked) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ext-benchmark-unlinked">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Label>External Benchmark</Label>
            <p className="mt-1 text-sm font-semibold text-slate-800">Not linked to Google yet</p>
            <p className="mt-1 text-xs text-slate-500">We'll auto-match this location using its saved address.</p>
          </div>
          {canEdit && (
            <Button size="sm" onClick={match} disabled={busy} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="ext-benchmark-match">
              <Link2 className="mr-1 h-4 w-4" /> {busy ? "Matching…" : "Match to Google"}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ----- linked, show the comparison -----
  const area = data.area_benchmark;
  const segment = data.segment_benchmark;
  const ssi5 = ssi != null ? Math.round((ssi / 20) * 10) / 10 : null; // ssi is 0-100 → 0-5
  const synced = data.last_synced ? new Date(data.last_synced) : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="ext-benchmark">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Label>External Benchmark</Label>
          <p className="mt-1 font-head text-base font-semibold text-slate-900" data-testid="ext-benchmark-place">
            {data.display_name || "Linked Google place"}
          </p>
          {data.formatted_address && (
            <p className="text-xs text-slate-500">{data.formatted_address}</p>
          )}
        </div>
        {canEdit && configured && (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="ext-benchmark-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            </Button>
            <Button size="sm" variant="outline" onClick={unlink} disabled={busy} data-testid="ext-benchmark-unlink">
              <Link2Off className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile
          label="Your SSI"
          value={ssi5}
          sub={ssi != null ? `${ssi}/100` : "—"}
          accent="#4338CA"
          testId="ext-tile-ssi"
          onClick={() => navigate(`/app/feedback?location=${locationId}`)}
        />
        <Tile
          label="Google rating"
          value={data.rating}
          sub={data.user_rating_count != null ? `${data.user_rating_count.toLocaleString()} reviews` : "—"}
          accent="#F59E0B"
          delta={ssi5 != null && data.rating != null ? ssi5 - data.rating : null}
          testId="ext-tile-google"
          loading={drillBusy === "google"}
          onClick={data.reviews && data.reviews.length ? () => openDrill("google") : null}
        />
        <Tile
          label="Area avg"
          value={area?.average_rating}
          sub={area?.count ? `${area.count} nearby` : "—"}
          accent="#10B981"
          delta={ssi5 != null && area?.average_rating != null ? ssi5 - area.average_rating : null}
          testId="ext-tile-area"
          loading={drillBusy === "area"}
          onClick={area?.samples?.length ? () => openDrill("area") : null}
        />
        <Tile
          label="Segment avg"
          value={segment?.average_rating}
          sub={segment?.count ? `${segment.count} sampled` : "—"}
          accent="#0EA5E9"
          delta={ssi5 != null && segment?.average_rating != null ? ssi5 - segment.average_rating : null}
          testId="ext-tile-segment"
          loading={drillBusy === "segment"}
          onClick={segment?.samples?.length ? () => openDrill("segment") : null}
        />
      </div>

      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span>
          {synced ? `Last synced ${synced.toLocaleDateString()}` : "Not yet synced"} · auto-refreshes weekly
        </span>
        {data.place_id && (
          <a
            href={`https://www.google.com/maps/place/?q=place_id:${data.place_id}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
            data-testid="ext-benchmark-maps-link"
          >
            Open in Maps <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>

      <ReviewsDialog open={reviewsOpen} onOpenChange={setReviewsOpen} reviews={data.reviews} placeName={data.display_name} />
      <PeersDialog
        open={areaOpen}
        onOpenChange={setAreaOpen}
        title="Area peers · within 10 km"
        subtitle={area?.count ? `${area.count} nearby businesses in the same category` : null}
        yourRating={data.rating}
        samples={area?.samples}
      />
      <PeersDialog
        open={segmentOpen}
        onOpenChange={setSegmentOpen}
        title={`Segment peers · ${segment?.scope || "country-wide"}`}
        subtitle={segment?.count ? `Top ${segment.samples?.length || 0} businesses sampled from ${segment.count} in your segment` : null}
        yourRating={data.rating}
        samples={segment?.samples}
      />
    </div>
  );
}

function ReviewsDialog({ open, onOpenChange, reviews, placeName }) {
  const [sort, setSort] = useState("newest"); // newest | lowest | highest
  const sorted = useMemo(() => {
    if (!reviews) return [];
    const copy = [...reviews];
    if (sort === "lowest") copy.sort((a, b) => (a.rating ?? 5) - (b.rating ?? 5));
    else if (sort === "highest") copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    else copy.sort((a, b) => (b.publish_time || "").localeCompare(a.publish_time || ""));
    return copy;
  }, [reviews, sort]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ext-reviews-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            Latest Google reviews · {placeName || "Store"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
          <p className="text-[11px] text-slate-500">Showing {sorted.length} review{sorted.length === 1 ? "" : "s"}</p>
          <div className="flex items-center gap-1 text-[11px]">
            <ArrowUpDown className="h-3 w-3 text-slate-400" />
            <SortChip active={sort === "newest"} onClick={() => setSort("newest")} label="Newest" testId="sort-newest" />
            <SortChip active={sort === "lowest"} onClick={() => setSort("lowest")} label="Lowest rating" testId="sort-lowest" />
            <SortChip active={sort === "highest"} onClick={() => setSort("highest")} label="Highest rating" testId="sort-highest" />
          </div>
        </div>
        {(!sorted || sorted.length === 0) ? (
          <p className="py-6 text-center text-sm text-slate-500">No reviews pulled yet. Refresh the benchmark.</p>
        ) : (
          <ul className="max-h-[70vh] space-y-3 overflow-y-auto">
            {sorted.map((r, i) => (
              <li key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`ext-review-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {r.author_photo ? (
                      <img src={r.author_photo} alt="" referrerPolicy="no-referrer"
                           className="h-7 w-7 flex-shrink-0 rounded-full border border-slate-200 object-cover" />
                    ) : (
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-600">
                        {(r.author_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <a href={r.author_uri || "#"} target="_blank" rel="noreferrer"
                       className="truncate text-sm font-semibold text-slate-700 hover:text-indigo-600 hover:underline">
                      {r.author_name || "Google user"}
                    </a>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {r.rating ?? "—"}
                    </span>
                    {r.relative_time && <span className="text-[11px] text-slate-400">{r.relative_time}</span>}
                  </div>
                </div>
                {r.text && (
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{r.text}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SortChip({ active, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full px-2 py-0.5 font-semibold transition ${active ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
    >
      {label}
    </button>
  );
}

function PeersDialog({ open, onOpenChange, title, subtitle, yourRating, samples }) {
  const [sort, setSort] = useState("highest"); // highest | lowest | reviews
  const sorted = useMemo(() => {
    if (!samples) return [];
    const copy = [...samples];
    if (sort === "lowest") copy.sort((a, b) => (a.rating ?? 5) - (b.rating ?? 5));
    else if (sort === "reviews") copy.sort((a, b) => (b.user_rating_count ?? 0) - (a.user_rating_count ?? 0));
    else copy.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    return copy;
  }, [samples, sort]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ext-peers-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {subtitle && <p className="-mt-2 text-xs text-slate-500">{subtitle}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
          <p className="text-[11px] text-slate-500">
            {yourRating != null && (<>Your Google rating: <span className="font-bold text-amber-700">{yourRating.toFixed(1)}★</span></>)}
          </p>
          <div className="flex items-center gap-1 text-[11px]">
            <ArrowUpDown className="h-3 w-3 text-slate-400" />
            <SortChip active={sort === "highest"} onClick={() => setSort("highest")} label="Highest" testId="sort-peers-highest" />
            <SortChip active={sort === "lowest"} onClick={() => setSort("lowest")} label="Lowest" testId="sort-peers-lowest" />
            <SortChip active={sort === "reviews"} onClick={() => setSort("reviews")} label="Most reviews" testId="sort-peers-reviews" />
          </div>
        </div>
        {(!sorted || sorted.length === 0) ? (
          <p className="py-6 text-center text-sm text-slate-500">No peer data yet. Refresh the benchmark.</p>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
            {sorted.map((s, i) => {
              const youBeat = yourRating != null && s.rating != null ? yourRating >= s.rating : null;
              return (
                <li key={s.place_id || i} className="flex items-center justify-between gap-3 py-2" data-testid={`ext-peer-${i}`}>
                  <div className="min-w-0">
                    <a
                      href={s.place_id ? `https://www.google.com/maps/place/?q=place_id:${s.place_id}` : "#"}
                      target="_blank" rel="noreferrer"
                      className="truncate text-sm font-semibold text-slate-800 hover:text-indigo-600 hover:underline"
                    >
                      {s.name || "—"}
                    </a>
                    <p className="text-[11px] text-slate-500">
                      {s.user_rating_count != null ? `${s.user_rating_count.toLocaleString()} reviews` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-bold text-amber-700">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                      {s.rating != null ? Number(s.rating).toFixed(1) : "—"}
                    </span>
                    {youBeat != null && (
                      <span className={`text-[10px] font-semibold ${youBeat ? "text-emerald-600" : "text-red-600"}`}>
                        {youBeat ? "you ≥" : "you <"}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value, sub, accent, delta, testId, onClick, loading = false }) {
  const display = value != null && !Number.isNaN(value) ? Number(value).toFixed(1) : "—";
  const clickable = typeof onClick === "function";
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      type={clickable ? "button" : undefined}
      onClick={clickable && !loading ? onClick : undefined}
      disabled={loading}
      className={`rounded-lg border border-slate-100 bg-slate-50 p-3 text-left ${clickable ? "cursor-pointer transition hover:border-amber-300 hover:bg-amber-50/60" : ""} ${loading ? "opacity-70" : ""}`}
      data-testid={testId}
    >
      <p className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <span>{label}</span>
        {loading ? <Loader2 className="h-3 w-3 animate-spin text-amber-500" />
          : clickable && <MousePointerClick className="h-3 w-3 text-amber-500" />}
      </p>
      <p className="mt-1 flex items-end gap-1">
        <span className="font-head text-2xl font-bold" style={{ color: accent }}>{display}</span>
        {value != null && <Star className="mb-1 h-3 w-3 text-amber-400" fill="currentColor" />}
      </p>
      <p className="text-[11px] text-slate-500">
        {loading ? "Fetching latest from Google…" : sub}
      </p>
      {delta != null && !loading && (
        <p className={`mt-1 text-[11px] font-semibold ${delta >= 0 ? "text-emerald-600" : "text-red-600"}`}>
          {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)} vs you
        </p>
      )}
    </Comp>
  );
}
